import React, { useRef, useEffect, useState } from "react";
import FloatingOrbs from "../decor/FloatingOrbs";
import "./Hero.css";

/**
 * Hero — Responsive Interactive 3D Neural Network
 *
 * A cursor-responsive neural-network animation rendered on a single HTML5
 * canvas. Floating nodes sit at varying depths (pseudo-3D), are connected by
 * animated lines, and react subtly to mouse movement via parallax + a gentle
 * repulsion field around the cursor.
 *
 * Design goals:
 *  - Lightweight & performant: dependency-free Canvas2D, capped node count,
 *    devicePixelRatio-aware, pauses when off-screen, honours reduced-motion.
 *  - Premium feel: smoothed (lerped) pointer, depth-based size/opacity/glow.
 *  - Readability first: a dark gradient veil keeps the overlaid copy legible.
 *
 * Spline-inspired interaction without the weight of a 3D engine.
 */

// Brand palette — swampy green/teal (src/noser.colors.js)
const PALETTE = [
  { r: 11, g: 171, b: 100 },  // green  #0bab64
  { r: 59, g: 183, b: 143 },  // teal   #3bb78f
  { r: 111, g: 224, b: 180 }, // mint   #6fe0b4
];

const Hero = ({
  title = (
    <>
      One connected
      <br />
      business platform
    </>
  ),
  subtitle = "Finance, people, sales, inventory, procurement and analytics — all in one system. Every transaction lands in one place, so dashboard you see is the truth you can act on.",
  cta,
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const rafRef = useRef(0);

  // Mutable animation state kept in a ref so re-renders never touch the loop.
  const stateRef = useRef({
    nodes: [],
    w: 0,
    h: 0,
    dpr: 1,
    // pointer in normalized [-1, 1] space; target = raw, current = smoothed
    pointer: { tx: 0, ty: 0, cx: 0, cy: 0, active: false, px: 0, py: 0 },
    running: false,
    reduced: false,
  });

  const [ready, setReady] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext("2d", { alpha: true });
    const S = stateRef.current;

    const reduceMQ = window.matchMedia("(prefers-reduced-motion: reduce)");
    S.reduced = reduceMQ.matches;

    // ---- sizing ---------------------------------------------------------
    const resize = () => {
      const rect = container.getBoundingClientRect();
      S.dpr = Math.min(window.devicePixelRatio || 1, 2); // cap dpr for perf
      S.w = rect.width;
      S.h = rect.height;
      canvas.width = Math.floor(S.w * S.dpr);
      canvas.height = Math.floor(S.h * S.dpr);
      canvas.style.width = S.w + "px";
      canvas.style.height = S.h + "px";
      ctx.setTransform(S.dpr, 0, 0, S.dpr, 0, 0);
      buildNodes();
    };

    // ---- node field -----------------------------------------------------
    const buildNodes = () => {
      // Scale node count with area but keep it bounded for performance.
      const area = S.w * S.h;
      const count = Math.max(28, Math.min(80, Math.round(area / 22000)));
      const nodes = [];
      for (let i = 0; i < count; i++) {
        const z = Math.random(); // 0 (far) .. 1 (near) — drives pseudo-3D
        const color = PALETTE[i % PALETTE.length];
        nodes.push({
          x: Math.random() * S.w,
          y: Math.random() * S.h,
          z,
          // slow drift velocity, faster when nearer the viewer
          vx: (Math.random() - 0.5) * (0.12 + z * 0.18),
          vy: (Math.random() - 0.5) * (0.12 + z * 0.18),
          r: 1.1 + z * 2.6,
          color,
          // per-node twinkle phase
          phase: Math.random() * Math.PI * 2,
        });
      }
      S.nodes = nodes;
    };

    // ---- pointer --------------------------------------------------------
    const onMove = (e) => {
      const rect = container.getBoundingClientRect();
      const point = e.touches ? e.touches[0] : e;
      const x = point.clientX - rect.left;
      const y = point.clientY - rect.top;
      S.pointer.px = x;
      S.pointer.py = y;
      S.pointer.tx = (x / S.w) * 2 - 1; // -1 .. 1
      S.pointer.ty = (y / S.h) * 2 - 1;
      S.pointer.active = true;
    };
    const onLeave = () => {
      S.pointer.active = false;
      S.pointer.tx = 0;
      S.pointer.ty = 0;
    };

    // ---- render loop ----------------------------------------------------
    const CONNECT_DIST = 150; // px threshold for drawing a link
    const CONNECT_SQ = CONNECT_DIST * CONNECT_DIST;

    const render = (t) => {
      const { nodes, w, h, pointer } = S;

      // smooth the pointer for a premium, weighted feel
      pointer.cx += (pointer.tx - pointer.cx) * 0.06;
      pointer.cy += (pointer.ty - pointer.cy) * 0.06;

      // parallax: the whole field shifts opposite the cursor, by depth
      const parX = -pointer.cx * 26;
      const parY = -pointer.cy * 26;

      ctx.clearRect(0, 0, w, h);

      // advance + project nodes into screen positions for this frame
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];

        if (!S.reduced) {
          n.x += n.vx;
          n.y += n.vy;
        }

        // wrap around edges with a small margin
        if (n.x < -20) n.x = w + 20;
        else if (n.x > w + 20) n.x = -20;
        if (n.y < -20) n.y = h + 20;
        else if (n.y > h + 20) n.y = -20;

        // depth parallax + cursor repulsion
        const depth = 0.4 + n.z; // nearer nodes move more
        let sx = n.x + parX * depth;
        let sy = n.y + parY * depth;

        if (pointer.active) {
          const dx = sx - pointer.px;
          const dy = sy - pointer.py;
          const d2 = dx * dx + dy * dy;
          const R = 140;
          if (d2 < R * R && d2 > 0.0001) {
            const d = Math.sqrt(d2);
            const force = (1 - d / R) * 18 * depth; // push outward
            sx += (dx / d) * force;
            sy += (dy / d) * force;
          }
        }

        n.sx = sx;
        n.sy = sy;
      }

      // draw connections (depth/distance-faded)
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const dx = a.sx - b.sx;
          const dy = a.sy - b.sy;
          const d2 = dx * dx + dy * dy;
          if (d2 > CONNECT_SQ) continue;
          const d = Math.sqrt(d2);
          const closeness = 1 - d / CONNECT_DIST;
          const depth = (a.z + b.z) * 0.5;
          const alpha = closeness * closeness * (0.18 + depth * 0.32);
          const c = a.color;
          ctx.strokeStyle = `rgba(${c.r},${c.g},${c.b},${alpha.toFixed(3)})`;
          ctx.lineWidth = 0.6 + depth * 0.9;
          ctx.beginPath();
          ctx.moveTo(a.sx, a.sy);
          ctx.lineTo(b.sx, b.sy);
          ctx.stroke();
        }
      }

      // draw nodes (with soft glow for the nearer ones)
      const time = t * 0.001;
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        const twinkle = 0.7 + 0.3 * Math.sin(time * 1.5 + n.phase);
        const c = n.color;
        const radius = n.r * (0.85 + n.z * 0.5);

        // glow halo
        const glow = ctx.createRadialGradient(
          n.sx, n.sy, 0,
          n.sx, n.sy, radius * 6
        );
        glow.addColorStop(0, `rgba(${c.r},${c.g},${c.b},${(0.20 * n.z * twinkle).toFixed(3)})`);
        glow.addColorStop(1, `rgba(${c.r},${c.g},${c.b},0)`);
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(n.sx, n.sy, radius * 6, 0, Math.PI * 2);
        ctx.fill();

        // core
        ctx.fillStyle = `rgba(${c.r},${c.g},${c.b},${(0.55 + 0.45 * n.z * twinkle).toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(n.sx, n.sy, radius, 0, Math.PI * 2);
        ctx.fill();
      }

      // highlight a link from the cursor to the nearest few nodes
      if (pointer.active) {
        for (let i = 0; i < nodes.length; i++) {
          const n = nodes[i];
          const dx = n.sx - pointer.px;
          const dy = n.sy - pointer.py;
          const d2 = dx * dx + dy * dy;
          if (d2 > CONNECT_SQ) continue;
          const d = Math.sqrt(d2);
          const alpha = (1 - d / CONNECT_DIST) * 0.5;
          const c = n.color;
          ctx.strokeStyle = `rgba(${c.r},${c.g},${c.b},${alpha.toFixed(3)})`;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(pointer.px, pointer.py);
          ctx.lineTo(n.sx, n.sy);
          ctx.stroke();
        }
      }

      rafRef.current = requestAnimationFrame(render);
    };

    const start = () => {
      if (S.running) return;
      S.running = true;
      rafRef.current = requestAnimationFrame(render);
    };
    const stop = () => {
      S.running = false;
      cancelAnimationFrame(rafRef.current);
    };

    // pause when the hero scrolls out of view (saves CPU/battery)
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) start();
        else stop();
      },
      { threshold: 0.01 }
    );

    resize();
    setReady(true);
    io.observe(container);

    window.addEventListener("resize", resize);
    container.addEventListener("mousemove", onMove);
    container.addEventListener("touchmove", onMove, { passive: true });
    container.addEventListener("mouseleave", onLeave);
    container.addEventListener("touchend", onLeave);

    const onReduce = (e) => {
      S.reduced = e.matches;
    };
    if (reduceMQ.addEventListener) reduceMQ.addEventListener("change", onReduce);
    else if (reduceMQ.addListener) reduceMQ.addListener(onReduce);

    return () => {
      stop();
      io.disconnect();
      window.removeEventListener("resize", resize);
      container.removeEventListener("mousemove", onMove);
      container.removeEventListener("touchmove", onMove);
      container.removeEventListener("mouseleave", onLeave);
      container.removeEventListener("touchend", onLeave);
      if (reduceMQ.removeEventListener) reduceMQ.removeEventListener("change", onReduce);
      else if (reduceMQ.removeListener) reduceMQ.removeListener(onReduce);
    };
  }, []);

  return (
    <section className="hero-nn" ref={containerRef}>
      {/* layered depth: floating orbs + faint perspective grid behind the canvas */}
      <FloatingOrbs className="hero-nn__orbs" grid />
      <canvas
        ref={canvasRef}
        className={`hero-nn__canvas ${ready ? "is-ready" : ""}`}
        aria-hidden="true"
      />
      <div className="hero-nn__veil" />
      <div className="hero-nn__content">
        <span className="hero-nn__eyebrow">Noser Technology</span>
        <h1 className="hero-nn__title">{title}</h1>
        <p className="hero-nn__subtitle">{subtitle}</p>
        {cta && <div className="hero-nn__cta">{cta}</div>}
      </div>
    </section>
  );
};

export default Hero;
